import Decimal from "decimal.js";

import { LENDER_FEE } from "../constants/fixed_values";
import {
  PERCENT_CREDITED_FHA,
  PERCENT_CREDITED_AGENCIES
} from "../constants/reserves_percentage_credited";

import {
  non_real_estate_balance_pay_offs_total,
  monthly_pitia_total,
  reserve_total_payment,
  residence_total_payment
} from "./debt_info";
import { total_payment } from "./investment_reo";
import {
  PRIMARY_RESIDENCE,
  SECOND_HOME,
  VACATION_HOME
} from "../constants/property_classification";
import {
  TYPE_ASSET,
  TYPE_RETIREMENT
} from "../constants/reserves_account_types";
import {
  full_month_of_interest_for_fha_loan,
  jumbo_reserves_required_total,
  liquid_reserves_after_close_of_escrow,
  loan_amount_first_mortgage,
  total_monthly_payment
} from "./loan";
import * as LIABILITY_TYPE from "../constants/liability_types";
import {
  AGENCY_LOAN_TYPES,
  JUMBO,
  HELOC,
  FIXED_RATE_SECOND
} from "../constants/loan_types";
import {
  first_current_monthly_payment,
  kept_home_breakdown,
  kept_home_monthly_total
} from "./property";
import {
  RETAINED,
  INVESTMENT,
  PRIMARY as PRIMARY_OCCUPANCY,
  SECONDARY as SECONDARY_OCCUPANCY
} from "../constants/property_disposition";
import { is_fha } from "../constants/utils";
import { counted_reo_properties, normalized_address } from "./reo_dedupe";
import { PURCHASE, REFINANCE } from "../constants/document_types";
import { YES } from "../constants/yes_no";

export function liquid_funds(total_funds, percentage_credited) {
  // A reserve whose sub-type is blank or unrecognised has no entry in the
  // percent-credited tables, so percentage_credited arrives as undefined and
  // Decimal.times(undefined) THROWS. With no error boundary that used to blank
  // the entire Reserves and Down Payment pages, and reloading never helped
  // because the same loan file failed the same way every time.
  //
  // Treat unknown as 0% credited rather than 100%: showing $0 credited makes
  // the missing sub-type visible so the LO can fix it, whereas silently
  // crediting the full amount would overstate qualifying reserves. This
  // matches the existing "|| 0" guard in edit_reserve.js.
  const funds = Number.isFinite(parseFloat(total_funds)) ? total_funds : 0;
  const percent = Number.isFinite(parseFloat(percentage_credited))
    ? percentage_credited
    : 0;

  return new Decimal(funds).times(percent).dividedBy(100);
}

export function lender_fees_estimate(property_to_be_sold) {
  let total = new Decimal(0);
  const firstMonthlyPayment = first_current_monthly_payment(
    property_to_be_sold
  );
  const secondMonthlyPayment = new Decimal(
    property_to_be_sold.second_current_monthly_payment
  );
  if (!firstMonthlyPayment.equals(0)) {
    total = total.plus(firstMonthlyPayment).plus(LENDER_FEE);
  }
  if (!secondMonthlyPayment.equals(0)) {
    total = total.plus(secondMonthlyPayment).plus(LENDER_FEE);
  }
  return total;
}

export function sum_total_loans_fees_payoff(
  property_to_be_sold,
  accountSummaries,
  residences,
  loan,
  is_selling_property
) {
  // Only NON-real-estate debts (cars/cards) paid from proceeds — the sold home's
  // mortgages are already subtracted as first/second_loan_balance below, so
  // including them here would double-count the payoff.
  const debt_payoff = loan
    ? non_real_estate_balance_pay_offs_total(
        Object.values(accountSummaries),
        residences,
        loan
      )
    : new Decimal(0);
  return new Decimal(property_to_be_sold.first_loan_balance)
    .plus(property_to_be_sold.second_loan_balance)
    .plus(property_to_be_sold.lender_fees)
    .plus(lender_fees_estimate(property_to_be_sold))
    .plus(property_to_be_sold.hoa_transfer_fees)
    .plus(property_to_be_sold.pay_off_back_taxes)
    .plus(property_to_be_sold.seller_concessions)
    .plus(debt_payoff)
    .plus(property_to_be_sold.rebate_back_to_borrower_closing_costs)
    .plus(
      full_month_of_interest_for_fha_loan(
        is_selling_property,
        property_to_be_sold
      )
    );
}

export function estimate_seller_closing_costs(property_to_be_sold) {
  return new Decimal(property_to_be_sold.current_market_value)
    .mul(property_to_be_sold.percent_seller_closing_costs)
    .div(100);
}

export function estimate_real_estate_fees(property_to_be_sold) {
  return new Decimal(property_to_be_sold.current_market_value)
    .mul(property_to_be_sold.real_estate_commission)
    .div(100);
}

export function net_from_sale(
  property_to_be_sold,
  residences,
  is_selling_property
) {
  return new Decimal(property_to_be_sold.current_market_value)
    .sub(
      sum_total_loans_fees_payoff(
        property_to_be_sold,
        null,
        residences,
        null,
        is_selling_property
      )
    )
    .sub(estimate_seller_closing_costs(property_to_be_sold))
    .sub(estimate_real_estate_fees(property_to_be_sold));
}

// The borrower is KEEPING their current home (Applicant Info: Borrower 1's
// Status = Retained). A kept home is never sold, so it produces no sale money:
// its balance vs. market value is equity only and must not be counted.
export function current_residence_retained(borrowers) {
  const primary = (borrowers || [])[0];
  return !!primary && primary.residence_disposition_status === RETAINED;
}

export function equity_from_pending_sale(
  documentType,
  property_to_be_sold,
  accountSummaries,
  residences,
  loan,
  is_selling_property,
  borrowers
) {
  if (documentType === REFINANCE) {
    return new Decimal(0);
  }
  // A kept (Retained) home has no sale. Without this, a kept home whose loan
  // balances were entered on the Selling Property page produced a NEGATIVE "sale"
  // (0 sale price minus the balances) that silently lowered Total Reserves.
  if (current_residence_retained(borrowers)) {
    return new Decimal(0);
  }
  return new Decimal(property_to_be_sold.current_market_value)
    .minus(
      sum_total_loans_fees_payoff(
        property_to_be_sold,
        accountSummaries,
        residences,
        loan,
        is_selling_property
      )
    )
    .minus(estimate_seller_closing_costs(property_to_be_sold))
    .minus(estimate_real_estate_fees(property_to_be_sold));
}

function conventional_reserve_total_for_borrower(total, borrower, reserveType) {
  return total.plus(
    (borrower.reserves || []).reduce((accum, reserve) => {
      if (reserveType && reserve.type !== reserveType) {
        return accum;
      }
      return accum.plus(
        liquid_funds(reserve.funds, PERCENT_CREDITED_AGENCIES[reserve.sub_type])
      );
    }, new Decimal(0))
  );
}

function fha_reserve_total_for_borrower(total, borrower, reserveType) {
  return total.plus(
    (borrower.reserves || []).reduce((accum, reserve) => {
      if (reserveType && reserve.type !== reserveType) {
        return accum;
      }
      return accum.plus(
        liquid_funds(reserve.funds, PERCENT_CREDITED_FHA[reserve.sub_type])
      );
    }, new Decimal(0))
  );
}

export function calculate_reserve_total(
  documentType,
  borrowers,
  accountSummaries,
  residences,
  property_to_be_sold,
  loan,
  is_selling_property,
  reserveType
) {
  // No loan selected yet (early-workflow file) -> compute conventional so blank
  // files render 0 instead of crashing on loan.type.
  if (loan && is_fha(loan.type)) {
    return total_fha_reserves(
      documentType,
      borrowers,
      property_to_be_sold,
      accountSummaries,
      residences,
      loan,
      is_selling_property,
      reserveType
    );
  }
  return total_conventional_reserves(
    documentType,
    borrowers,
    property_to_be_sold,
    accountSummaries,
    residences,
    loan,
    is_selling_property,
    reserveType
  );
}

export function total_conventional_reserves(
  documentType,
  borrowers,
  property_to_be_sold,
  accountSummaries,
  residences,
  loan,
  is_selling_property,
  reserveType
) {
  return borrowers
    .reduce(
      (total, borrower) =>
        conventional_reserve_total_for_borrower(total, borrower, reserveType),
      new Decimal(0)
    )
    .plus(
      reserveType && reserveType !== TYPE_ASSET
        ? 0
        : equity_from_pending_sale(
          documentType,
          property_to_be_sold,
          accountSummaries,
          residences,
          loan,
          is_selling_property,
          borrowers
        )
    );
}

export function total_fha_reserves(
  documentType,
  borrowers,
  property_to_be_sold,
  accountSummaries,
  residences,
  loan,
  is_selling_property,
  reserveType
) {
  return borrowers
    .reduce(
      (total, borrower) =>
        fha_reserve_total_for_borrower(total, borrower, reserveType),
      new Decimal(0)
    )
    .plus(
      reserveType && reserveType !== TYPE_ASSET
        ? 0
        : equity_from_pending_sale(
          documentType,
          property_to_be_sold,
          accountSummaries,
          residences,
          loan,
          is_selling_property,
          borrowers
        )
    );
}

/**
 * The Invest REO rows that count for reserves - each property ONCE (see
 * counted_reo_properties: refi subject row skipped, duplicate addresses
 * dropped, LANIS's kept-home row wins).
 */
export function reserve_reo_properties(investment_properties) {
  return counted_reo_properties(investment_properties);
}

/** A Debt page residence that is the same home as a counted Invest REO row. */
export function residence_duplicates_reo(residence, investment_properties) {
  const addr = normalized_address(residence && residence.address);
  if (!addr) return false;
  return reserve_reo_properties(investment_properties).some(
    p => normalized_address(p.property_address) === addr
  );
}

export function calculations_percentage(investment_properties, residences) {
  // There is always a subject property. Each other property counts ONCE: an
  // Invest REO row and a Debt page residence at the same address are the same
  // home (counting it twice pushed files from 2% to 4%).
  const subjectPropertyCount = 1;
  const reo = reserve_reo_properties(investment_properties);
  const reoAddresses = new Set(
    reo.map(p => normalized_address(p.property_address)).filter(Boolean)
  );
  const residenceAddresses = new Set();
  const residenceCount = Object.values(residences || {}).reduce(
    (count, residence) => {
      if (residence.classification === PRIMARY_RESIDENCE) return count;
      const addr = normalized_address(residence.address);
      if (addr && (reoAddresses.has(addr) || residenceAddresses.has(addr))) {
        return count;
      }
      if (addr) residenceAddresses.add(addr);
      return count + 1;
    },
    0
  );
  const propertyCount = subjectPropertyCount + reo.length + residenceCount;
  if (propertyCount <= 4) {
    return new Decimal(2);
  } else if (propertyCount <= 6) {
    return new Decimal(4);
  }
  return new Decimal(6);
}

export function monthly_percent_reserves(
  investment_property,
  investment_properties,
  residences
) {
  return new Decimal(investment_property.principal_balance)
    .mul(calculations_percentage(investment_properties, residences))
    .div(100);
}

/**
 * Summary Sheet - Total of Liquid and Retirement Accounts
 */
export function total_of_liquid_and_retirement_accounts(
  documentType,
  borrowers,
  accountSummaries,
  residences,
  property_to_be_sold,
  loan,
  is_selling_property
) {
  return total_reserves_available(
    documentType,
    borrowers,
    accountSummaries,
    residences,
    property_to_be_sold,
    loan,
    is_selling_property
  ).add(
    calculate_reserve_total(
      documentType,
      borrowers,
      accountSummaries,
      residences,
      property_to_be_sold,
      loan,
      is_selling_property,
      TYPE_RETIREMENT
    )
  );
}

/**
 * Reserves - Total Reserves Available
 */
export function total_reserves_available(
  documentType,
  borrowers,
  accountSummaries,
  residences,
  property_to_be_sold,
  loan,
  is_selling_property
) {
  // We want to include all types so this ends up the same as total reserves.
  return calculate_reserve_total(
    documentType,
    borrowers,
    accountSummaries,
    residences,
    property_to_be_sold,
    loan,
    is_selling_property
  );
}

/**
 * Reserves - Subject Property - Outstanding UPB
 */
export function subject_property_outstanding_upb(
  documentType,
  loan,
  property,
  property_to_be_sold,
  is_selling_property
) {
  return property.classification !== PRIMARY_RESIDENCE && loan
    ? loan_amount_first_mortgage(
      documentType,
      loan,
      property,
      property_to_be_sold,
      is_selling_property
    )
    : new Decimal(0);
}

/**
 * Reserves - Subject Property - Monthly PITIA
 */
export function subject_property_monthly_pitia(
  documentType,
  loan,
  property,
  property_to_be_sold,
  is_selling_property
) {
  return property.classification !== PRIMARY_RESIDENCE && loan
    ? total_monthly_payment(
      documentType,
      loan,
      property,
      property_to_be_sold,
      is_selling_property
    )
    : new Decimal(0);
}

/**
 * Reserves - Subject Property - Reserve Months
 */
export function subject_property_reserve_months(property) {
  if (property.classification === PRIMARY_RESIDENCE) {
    return new Decimal(0);
  }
  // Fannie Mae treats a Vacation Home as a second home: 2 months, not 6.
  if (
    property.classification === SECOND_HOME ||
    property.classification === VACATION_HOME
  ) {
    return new Decimal(2);
  }
  return new Decimal(6);
}

/**
 * Reserve months for an Invest REO row, tiered by its Intended Occupancy (agency
 * rule): Primary Residence = 0 (no reserves), Second Home = 2, Investment (or
 * unset/other) = 6. Mirrors subject_property_reserve_months but keyed on the REO
 * row's occupancy rather than the subject classification.
 */
export function reo_reserve_months(property) {
  if (property.intended_occupancy === PRIMARY_OCCUPANCY) {
    return 0;
  }
  if (property.intended_occupancy === SECONDARY_OCCUPANCY) {
    return 2;
  }
  return 6;
}

/**
 * Reserves - Subject Property - Monthly Reserves
 */
export function subject_property_monthly_reserves(
  documentType,
  loan,
  property,
  property_to_be_sold,
  is_selling_property
) {
  return subject_property_monthly_pitia(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  ).mul(subject_property_reserve_months(property));
}

/**
 * Reserves - Subject Property - Monthly % Reserves
 */
export function subject_property_monthly_percent_reserves(
  documentType,
  loan,
  property,
  investment_properties,
  residences,
  property_to_be_sold,
  is_selling_property
) {
  return subject_property_outstanding_upb(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  )
    .mul(calculations_percentage(investment_properties, residences))
    .div(100);
}

/**
 * Reserves - Subject Property - Total Reserves
 */
export function subject_property_total_reserves(
  documentType,
  loan,
  property,
  investment_properties,
  residences,
  property_to_be_sold,
  is_selling_property
) {
  // A retained Primary Residence subject needs no reserves (no PITIA months and
  // no financed-property UPB% - it's the borrower's home, not an investment).
  if (property.classification === PRIMARY_RESIDENCE) {
    return new Decimal(0);
  }
  return subject_property_monthly_reserves(
    documentType,
    loan,
    property,
    property_to_be_sold,
    is_selling_property
  ).add(
    subject_property_monthly_percent_reserves(
      documentType,
      loan,
      property,
      investment_properties,
      residences,
      property_to_be_sold,
      is_selling_property
    )
  );
}

/**
 * Reserves - Total Reserve Requirement (Agency Reserves)
 */
// The borrower is RETAINING their current residence as an investment (set on
// Applicant Info: Status = Retained, Intended Occupancy = Investment). It then
// needs reserves like any Non-Owner property.
export function current_residence_retained_investment(borrowers) {
  // The current residence belongs to the primary borrower - drive the treatment
  // off that borrower's Status/Occupancy, not `.some()` (which would stay true
  // if a co-borrower still had a stale Investment setting).
  const primary = (borrowers || [])[0];
  return (
    !!primary &&
    primary.residence_disposition_status === RETAINED &&
    primary.residence_intended_occupancy === INVESTMENT
  );
}

// UPB of the retained current residence = first + second + third loan balances
// (from the Selling Property page, which carries the full picture; the Debt-page
// credit line is P&I only).
export function retained_residence_upb(property_to_be_sold) {
  // "No mortgage" (free and clear) = no balance, whatever was typed earlier.
  return kept_home_breakdown(property_to_be_sold).principal_balance;
}

// Reserve requirement for a retained-as-investment current residence: 6 months
// of full PITI+HOA (from the Selling Property page) + the financed-property UPB
// percentage. Zero unless it is retained-investment.
export function retained_investment_reserve(
  borrowers,
  investment_properties,
  residences,
  property_to_be_sold
) {
  if (!current_residence_retained_investment(borrowers) || !property_to_be_sold) {
    return new Decimal(0);
  }
  // Same full monthly cost the Invest REO row carries (impound answer, MI, 3rd
  // lien, flood and Mello Roos included) so Reserves and DTI agree.
  const pitia = kept_home_monthly_total(property_to_be_sold);
  const upb = retained_residence_upb(property_to_be_sold);
  return reserve_total_payment(6, pitia).add(
    upb.mul(calculations_percentage(investment_properties, residences)).div(100)
  );
}

// Reserve-page include/exclude picker: the LO can uncheck a property to drop it
// from the reserve requirement (Non-QM flexibility). Excluded keys are global
// (all loans). Keys: "subject", "reo:<property_address>", "residence:<id>".
export function reserve_property_excluded(excluded, key) {
  return (excluded || []).includes(key);
}

export function total_reserve_requirement(
  documentType,
  loan,
  property,
  investment_properties,
  residences,
  accountSummaries,
  property_to_be_sold,
  is_selling_property,
  borrowers,
  excluded
) {
  const subject = reserve_property_excluded(excluded, "subject")
    ? new Decimal(0)
    : subject_property_total_reserves(
        documentType,
        loan,
        property,
        investment_properties,
        residences,
        property_to_be_sold,
        is_selling_property
      );
  // The retained-as-investment current residence is auto-added to the Invest REO
  // list, so its reserves flow through the investment_properties reduce below -
  // do NOT add it here too (that double-counted it).
  return subject
    .add(
      // Each property once (duplicate addresses dropped automatically).
      reserve_reo_properties(investment_properties).reduce((total, investment_property) => {
        // The refi SUBJECT property is already counted via
        // subject_property_total_reserves above - skip it here so it is never
        // double-counted as an Invest REO row (this was the retained-Primary
        // double-count: once as Subject, once as a Non-Owner REO row).
        if (investment_property.is_subject_property === YES) {
          return total;
        }
        if (
          reserve_property_excluded(
            excluded,
            "reo:" + investment_property.property_address
          )
        ) {
          return total;
        }
        // Tier reserves by the row's Intended Occupancy: Primary = none,
        // Second Home = 2 months, Investment = 6 months (+ financed UPB%).
        const months = reo_reserve_months(investment_property);
        if (months === 0) {
          return total;
        }
        total = total
          .add(
            monthly_percent_reserves(
              investment_property,
              investment_properties,
              residences
            )
          )
          .add(reserve_total_payment(months, total_payment(investment_property)));
        return total;
      }, new Decimal(0))
    )
    .add(
      Object.values(accountSummaries).reduce((total, accountSummary) => {
        // TODO: this is the same logic from the total_reserve_calculator, refactor it here and there.
        if (accountSummary.liability_type !== LIABILITY_TYPE.SECOND_HOME) {
          return total;
        }
        if (reserve_property_excluded(excluded, "residence:" + accountSummary.id)) {
          return total;
        }
        const residence = residences[accountSummary.residence];
        // Missing residence data, or the same home already counted as an
        // Invest REO row: count it once (on Invest REO).
        if (!residence || residence_duplicates_reo(residence, investment_properties)) {
          return total;
        }
        const residenceTotalPayment = residence_total_payment(
          residence.first_mortgage,
          residence.second_mortgage,
          residence.taxes,
          residence.insurance,
          residence.hoa
        );
        total = total
          .add(
            monthly_percent_reserves(
              residence,
              investment_properties,
              residences
            )
          )
          .add(reserve_total_payment(2, residenceTotalPayment));
        return total;
      }, new Decimal(0))
    );
}

/**
 * Reserves - Total Reserve Requirement (Jumbo Reserves)
 */
export function total_reserve_requirement_jumbo(
  loans,
  loan,
  investment_properties
) {
  if (!loan) {
    return new Decimal(0);
  }
  return loan.type === JUMBO
    ? monthly_pitia_total(loans, investment_properties).mul(
      jumbo_reserves_required_total(loan)
    )
    : new Decimal(0);
}

/**
 * Summary Sheet - Total Reserve Requirement
 */
export function total_reserve_requirement_for_loan_type(
  documentType,
  loans,
  loan,
  property,
  investment_properties,
  residences,
  accountSummaries,
  property_to_be_sold,
  is_selling_property,
  borrowers,
  excluded
) {
  // No loan selected yet (early-workflow / not-yet-configured file): nothing to
  // require reserves against -> 0, instead of crashing on loan.type.
  if (!loan) {
    return new Decimal(0);
  }
  // Agency + Jumbo compute below; also compute for 2nd-lien products
  // (HELOC / Fixed-Rate Second) - a 2nd from an agency lender can still require
  // reserves. The LO can zero it out with the Reserve-page include/exclude picker.
  if (
    AGENCY_LOAN_TYPES.includes(loan.type) ||
    loan.type === HELOC ||
    loan.type === FIXED_RATE_SECOND
  ) {
    return total_reserve_requirement(
      documentType,
      loan,
      property,
      investment_properties,
      residences,
      accountSummaries,
      property_to_be_sold,
      is_selling_property,
      borrowers,
      excluded
    );
  }
  if (loan.type === JUMBO) {
    return total_reserve_requirement_jumbo(loans, loan, investment_properties);
  }
  return new Decimal(0);
}

/**
 * Summary Sheet - Reserves (Positive or Shortage)
 */
export function reserves_positive_or_shortage(
  documentType,
  loans,
  borrowers,
  accountSummaries,
  residences,
  property_to_be_sold,
  loan,
  property,
  is_selling_property,
  investment_properties,
  excluded
) {
  const reservesAmount =
    documentType === PURCHASE
      ? liquid_reserves_after_close_of_escrow(
        documentType,
        borrowers,
        accountSummaries,
        residences,
        property_to_be_sold,
        loan,
        property,
        is_selling_property
      )
      : calculate_reserve_total(
        documentType,
        borrowers,
        accountSummaries,
        residences,
        property_to_be_sold,
        loan,
        is_selling_property
      );

  return reservesAmount.sub(
    total_reserve_requirement_for_loan_type(
      documentType,
      loans,
      loan,
      property,
      investment_properties,
      residences,
      accountSummaries,
      property_to_be_sold,
      is_selling_property,
      borrowers,
      excluded
    )
  );
}
